import type { DamageType, Vec2 } from '../types';
import type { Enemy } from '../entities/Enemy';
import type { Tower } from '../entities/Tower';
import type { GameSettings } from '../systems/SettingsManager';

export interface GameEventMap {
  'input:click': [Vec2 & { pixelX: number; pixelY: number; button: number }];
  'input:hover': [Vec2 & { pixelX: number; pixelY: number }];
  'input:key': [{ key: string }];
  'input:release': [Vec2];

  'tower:fire': [{ tower: Tower; target: Enemy; damage: number; damageType: DamageType }];
  'tower:placed': [{ x: number; y: number; towerId: string; tower: Tower }];
  'tower:placementFailed': [{ reason: string }];
  'tower:upgraded': [{ tower: Tower }];
  'tower:sold': [{ tower: Tower; refund: number }];

  'enemy:damaged': [{ enemy: Enemy; damage: number }];
  'enemy:killed': [{ enemy: Enemy; reward: number }];
  'enemy:reachedCore': [{ enemy: Enemy }];
  'enemy:healPulse': [{ source: Enemy; x: number; y: number; radius: number; amount: number }];
  'enemy:bomberExploded': [{ x: number; y: number; radius: number; towerStunDuration: number }];

  'boss:summon': [{ x: number; y: number; count: number }];
  'boss:burningGround': [{ x: number; y: number }];
  'boss:spawnFlyers': [{ x: number; y: number; count: number }];

  'effect:beam': [{ x1: number; y1: number; x2: number; y2: number; color: string }];
  'effect:explosion': [{ x: number; y: number; color: string; size: number }];
  'effect:hit': [{ x: number; y: number; color: string }];

  'wave:started': [number];
  'wave:complete': [{ wave: number; bonus: number; current: number }];
  'economy:changed': [number];

  'ui:startWave': [];
  'ui:selectTower': [{ towerId: string }];
  'ui:setSpeed': [{ scale: number }];

  'settings:masterVolume': [number];
  'settings:musicVolume': [number];
  'settings:sfxVolume': [number];
  'settings:quality': [GameSettings['quality']];
  'settings:pathPreview': [boolean];
  'settings:particles': [boolean];
  'settings:reset': [];
  'settings:changed': [GameSettings];
  'save:request': [];

  'menu:resume': [];
  'menu:restart': [];
  'menu:quit': [];
  'menu:levels': [];
  'menu:nextLevel': [{ levelId: string }];

  'game:complete': [{ levelId: string; victory: boolean; wave: number; lives: number; stars: number }];
}

type EventName = keyof GameEventMap;
type AnyListener = (...args: any[]) => void;

export class EventBus {
  private listeners = new Map<EventName, Set<AnyListener>>();

  public on<K extends EventName>(event: K, callback: (...args: GameEventMap[K]) => void): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    const listener = callback as AnyListener;
    this.listeners.get(event)!.add(listener);
    return () => this.listeners.get(event)?.delete(listener);
  }

  public emit<K extends EventName>(event: K, ...args: GameEventMap[K]): void {
    const callbacks = this.listeners.get(event);
    if (!callbacks) return;
    for (const callback of callbacks) callback(...args);
  }
}

export const eventBus = new EventBus();
